import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ArrowRight, GraduationCap, FilePlus2, BookOpen, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const CURRICULUM = [
  {
    day: "Day 1",
    label: "Foundations",
    duration: "6 hours · Lecture + Q&A",
    items: [
      "Overview of the NYS prison system: DOCCS structure, facility tiers, classification levels, and how decisions flow from the central office to the cellblock",
      "Constitutional and statutory rights of incarcerated individuals: Eighth Amendment standards, PLRA exhaustion requirements, Section 1983 basics",
      "The legal pathways toolkit: when to use 440.10 motions, Article 78 petitions, habeas, parole appeals, CORC grievance appeals, and federal civil-rights claims",
      "Reading a DOCCS record: time computations, jail-time credits, good-time/merit-time, and how to spot calculation errors",
      "Case study: an incarcerated individual facing a Tier 3 with parole implications — what the timeline looks like and where the leverage points are",
    ],
  },
  {
    day: "Day 2",
    label: "Documents & Systems",
    duration: "6 hours · Workshop format",
    items: [
      "Anatomy of an attorney-graded paralegal packet: cover page, table of contents, exhibits, certificate of service, and how each piece signals quality",
      "Drafting structured personal statements: the Accountability → Reflection → Rehabilitation → Growth → Future framework that converts intake notes into persuasive narrative",
      "Reentry plan craft: housing, employment, financial stability, community support, and risk reduction — what reviewers actually want to see",
      "Exhibit indexing and chain-of-custody: how to organize certificates, support letters, programmatic records, and medical documentation",
      "Workshop: build a complete parole packet from raw intake materials end-to-end, with peer review",
      "Common errors that get packets rejected — sloppy captions, missing residency language, wrong service method, mis-numbered paragraphs",
    ],
  },
  {
    day: "Day 3",
    label: "Practical Application",
    duration: "6 hours · Live case work + role-play",
    items: [
      "Live case walkthroughs: 2–3 real (de-identified) cases dissected from intake to final filing — what worked, what didn't, what we'd do differently",
      "Intake-to-document workflow end-to-end: client interview → fact pattern → legal theory → document drafting → quality review → delivery",
      "Working with incarcerated clients with empathy and professional boundaries: phone-call etiquette, written correspondence, family liaison, and trust-building",
      "Difficult conversations: what to do when a client's story doesn't match the record, when expectations don't match reality, and when to refer out",
      "Role-play: conduct a mock client intake call, then draft the resulting fact pattern in real time with feedback",
      "Q&A clinic: bring your own case — receive structured guidance from Darrell and peer participants",
    ],
  },
];

const REMOTE_PERKS = [
  "Live cohort sessions via Zoom (video on)",
  "Workbook + sample document library mailed in advance",
  "Recordings available for 90 days post-training",
  "Office hours: one 30-min 1:1 follow-up with Darrell",
];

export default function Attorneys() {
  const [form, setForm] = useState({ name: "", firm: "", email: "", phone: "", interest: "both", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/intake/attorney", form);
      setSubmitted(true);
      toast.success("Inquiry received. We'll be in touch within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-zinc-100">
      {/* Hero */}
      <section className="pt-16 pb-14 border-b border-zinc-900">
        <div className="container-pl max-w-4xl">
          <p className="overline mb-4">For attorneys & professionals</p>
          <h1 className="display-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 leading-[1.05] mb-5 accent-rule">
            Outsource document prep.<br/>Train your team. Scale your impact.
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl">
            Two B2B offerings built for law firms, public defenders, reentry orgs, and community advocates serving New York clients.
          </p>
        </div>
      </section>

      {/* Two-column offerings */}
      <section className="py-14">
        <div className="container-pl grid md:grid-cols-2 gap-6">
          {/* Doc prep */}
          <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-7" data-testid="attorney-docprep-card">
            <div className="h-11 w-11 rounded-sm border border-amber-400/40 bg-blue-950/30 flex items-center justify-center mb-5">
              <FilePlus2 className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
            </div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">Document Preparation Support</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              Hand off the formatting and assembly work so your attorneys focus on argument and strategy.
            </p>
            <ul className="space-y-2 text-sm text-zinc-300">
              {["Parole packets", "Case summaries", "Intake processing", "Exhibit organization", "440 / Article 78 support documents"].map((x) => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={1.5}/>{x}</li>
              ))}
            </ul>
          </div>

          {/* Training */}
          <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-7" data-testid="attorney-training-card">
            <div className="h-11 w-11 rounded-sm border border-amber-400/40 bg-blue-950/30 flex items-center justify-center mb-5">
              <GraduationCap className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
            </div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-2">Training Program</h2>
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-amber-400 mb-3">"Understanding Prison Law & Incarcerated Individual Support Systems"</p>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              A focused 3-day curriculum for legal teams, paralegals, and reentry advocates. 18 hours total of live instruction.
            </p>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400 border border-amber-400/40 rounded-sm px-2 py-1">Remote only</span>
              <span className="text-[11px] text-zinc-500">Live, synchronous · cohort-based</span>
            </div>
            <ul className="space-y-1.5 mt-2">
              {REMOTE_PERKS.map((p) => (
                <li key={p} className="text-xs text-zinc-300 flex items-start gap-2"><span className="text-amber-400 mt-1">•</span>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-14 border-t border-zinc-900 bg-zinc-950/40">
        <div className="container-pl">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-4 w-4 text-amber-400" strokeWidth={1.5}/>
            <p className="overline">Curriculum</p>
          </div>
          <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 mb-10 accent-rule">Three days. One transformative skillset.</h2>

          <div className="grid md:grid-cols-3 gap-5">
            {CURRICULUM.map((d) => (
              <div key={d.day} className="border border-zinc-800 bg-black/60 p-6 rounded-sm" data-testid={`curriculum-${d.day.replace(" ", "-").toLowerCase()}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400 mb-2">{d.day}</p>
                <h3 className="display-serif text-xl text-zinc-50 mb-1.5 leading-tight">{d.label}</h3>
                <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-4">{d.duration}</p>
                <ul className="space-y-2.5 text-sm text-zinc-300">
                  {d.items.map((it) => <li key={it} className="flex items-start gap-2 leading-relaxed"><span className="text-amber-400 mt-1.5 text-[8px]">●</span>{it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section className="py-14 border-t border-zinc-900" id="inquire">
        <div className="container-pl max-w-2xl">
          {submitted ? (
            <div className="border border-amber-400/30 bg-blue-950/20 rounded-sm p-8 text-center" data-testid="attorney-thanks">
              <ShieldCheck className="h-10 w-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5}/>
              <h2 className="display-serif text-3xl text-zinc-50 mb-2">Inquiry received.</h2>
              <p className="text-zinc-300">We'll respond within one business day with next steps.</p>
              <Link to="/services"><Button variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30 mt-6">Browse services <ArrowRight className="ml-2 h-4 w-4"/></Button></Link>
            </div>
          ) : (
            <>
              <p className="overline mb-4">Get in touch</p>
              <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 mb-3 accent-rule">Start the conversation.</h2>
              <p className="text-sm text-zinc-400 mb-8 max-w-lg">Tell us a bit about your firm or organization and we'll respond within one business day.</p>

              <form onSubmit={submit} className="space-y-5" data-testid="attorney-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Your name</Label>
                    <Input required value={form.name} onChange={(e) => update("name", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="att-name"/>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Firm / Organization</Label>
                    <Input value={form.firm} onChange={(e) => update("firm", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="att-firm"/>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Email</Label>
                    <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="att-email"/>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Phone</Label>
                    <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="att-phone"/>
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-300 mb-2 block">I'm interested in</Label>
                  <RadioGroup value={form.interest} onValueChange={(v) => update("interest", v)} className="grid sm:grid-cols-3 gap-2">
                    {[{v:"document_prep",l:"Document prep"},{v:"training",l:"Training program"},{v:"both",l:"Both"}].map(o => (
                      <Label key={o.v} className="flex items-center gap-2 border border-zinc-800 rounded-sm px-4 py-3 cursor-pointer hover:border-amber-400/60 transition-colors">
                        <RadioGroupItem value={o.v} className="border-amber-400/60 text-amber-400"/>
                        <span className="text-sm">{o.l}</span>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="text-zinc-300">Brief message</Label>
                  <Textarea rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="att-message" placeholder="Tell us about your needs."/>
                </div>

                <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="att-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send inquiry <ArrowRight className="ml-2 h-4 w-4"/></>}
                </Button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
